/**
 * Page CCV (Vérification des Contrôles Critiques - CCM) : liste triable/
 * filtrable/recherchable/exportable, avec détection automatique des retards.
 */
import { useEffect, useState } from "react";
import { dashboardApi } from "@/lib/api";
import { useFilters } from "@/contexts/FilterContext";
import useQuickFilter from "@/hooks/useQuickFilter";
import DataTable from "@/components/tables/DataTable";
import SummaryChip from "@/components/tables/SummaryChip";
import IdLinkCell from "@/components/tables/IdLinkCell";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PAGES, QUICK_FILTER_KPI } from "@/constants/testIds";

const COLUMNS = [
  { key: "id", label: "Numéro" },
  { key: "date", label: "Date" },
  { key: "site", label: "Site" },
  { key: "control_summary", label: "Résumé du contrôle" },
  { key: "auditor_name", label: "Auditeur" },
  { key: "status", label: "Statut" },
  { key: "due_date", label: "Échéance" },
  { key: "late", label: "Retard" },
];

const QUICK_FILTERS = {
  open: (r) => r.status === "Open",
  late: (r) => r.late,
};

export default function CCVPage() {
  const { filters, refreshKey } = useFilters();
  const [data, setData] = useState(null);
  const { active: quickFilter, toggle: toggleQuickFilter, clear: clearQuickFilter, apply: applyQuickFilter } = useQuickFilter(QUICK_FILTERS);

  useEffect(() => {
    dashboardApi.getCcv(filters).then(({ data }) => setData(data));
  }, [filters, refreshKey]);

  const renderCell = (col, row) => {
    if (col.key === "id") {
      return <IdLinkCell id={row.id} href={row.link} testId={`ccv-link-${row.id}`} />;
    }
    if (col.key === "status") {
      return (
        <Badge variant="outline" className={row.status === "Open" ? "border-amber-300 text-amber-700" : "border-emerald-300 text-emerald-700"}>
          {row.status}
        </Badge>
      );
    }
    if (col.key === "late") {
      return row.late ? <Badge className="border-none bg-red-100 text-red-700">En retard</Badge> : <span className="text-slate-400">-</span>;
    }
    if (col.key === "control_summary") {
      return (
        <span className="block max-w-[260px] truncate" title={row.control_summary}>
          {row.control_summary || "-"}
        </span>
      );
    }
    return undefined;
  };

  if (!data) return <Skeleton className="h-96 rounded-lg" />;

  return (
    <div data-testid={PAGES.ccv} className="space-y-4">
      <h1 className="text-xl font-bold text-slate-800">CCV — Vérification des Contrôles Critiques (CCM)</h1>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <SummaryChip testId={QUICK_FILTER_KPI.ccvTotal} label="Total" value={data.summary.total} onClick={clearQuickFilter} active={!quickFilter} />
        <SummaryChip testId={QUICK_FILTER_KPI.ccvOpen} label="Ouvertes" value={data.summary.open} tone="blue" onClick={() => toggleQuickFilter("open")} active={quickFilter === "open"} />
        <SummaryChip testId={QUICK_FILTER_KPI.ccvLate} label="En retard" value={data.summary.late} tone="orange" onClick={() => toggleQuickFilter("late")} active={quickFilter === "late"} />
      </div>
      <DataTable
        testId="ccv-table"
        title="CCV"
        columns={COLUMNS}
        data={applyQuickFilter(data.items)}
        renderCell={renderCell}
        defaultSort={[{ id: "late", desc: true }]}
      />
    </div>
  );
}
